import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, ChevronRight, Star, ShieldCheck, Zap, Trophy, Wallet, Headphones, BadgePercent, Activity, Spade, Plane, Users, BarChart3, CheckCircle2, Clock, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { useSite } from "../context/SiteContext";
import { FAQS, SERVICES, TESTIMONIALS, IMAGES, GAMES } from "../data/mock";

const ICONS = { Zap, ShieldCheck, Wallet, Trophy, Headphones, BadgePercent, Activity, Spade, Plane, Users, BarChart3 };
function Icon({ name, ...p }) {
  const C = ICONS[name] || Trophy;
  return <C {...p} />;
}

export default function Home() {
  const { site } = useSite();
  const { t } = useTranslation();
  const stats = site.stats || [];
  const features = site.features || [];
  const steps = site.steps || [];
  const exchanges = site.exchanges || [];

  return (
    <div>
      <SEO type="website" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroStadium} alt="Cricket stadium" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1226]/85 via-[#0a1226]/90 to-[#0a1226]" />
        </div>
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="fade-up">
              <Badge className="bg-amber-500/15 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full px-3 py-1 text-xs font-semibold">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t("hero.badge")}
              </Badge>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
                {t("hero.titlePart1")} <span className="brand-gradient">{site.brand}</span><br className="hidden sm:block" /> {t("hero.titlePart2")}
              </h1>
              <p className="mt-5 text-slate-300 text-base md:text-lg max-w-xl leading-relaxed">{t("hero.subtitle")}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm sm:text-base">
                  <MessageCircle className="w-5 h-5" /> {t("hero.ctaWhatsapp")}
                </a>
                <Link to="/how-to-play" className="btn-outline-gold inline-flex items-center gap-1.5 px-5 py-3 rounded-xl text-sm sm:text-base">
                  {t("hero.ctaHowTo")} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                  <span className="text-sm text-slate-300 ml-1">{t("hero.ratingLabel")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 live-dot inline-block" /> {t("hero.liveNow")}: <span className="text-amber-300 font-semibold">12 {t("hero.matches")}</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl shadow-black/40">
                <img src={IMAGES.modiStadium} alt="Stadium" className="w-full h-[340px] sm:h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1226] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/55 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
                  <span className="w-2 h-2 rounded-full bg-red-500 live-dot" />
                  <span className="text-xs font-semibold text-white">{t("hero.liveTag")}</span>
                </div>
                {stats.length >= 3 && (
                  <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-2">
                    {stats.slice(0, 3).map((s) => (
                      <div key={s.label} className="bg-black/50 backdrop-blur-md rounded-xl p-3 border border-white/10">
                        <div className="text-amber-400 font-extrabold text-lg sm:text-xl leading-none">{s.value}</div>
                        <div className="text-[10px] sm:text-[11px] text-slate-300 mt-1 uppercase tracking-wide">{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-[#101a35] border border-amber-500/30 rounded-2xl px-4 py-3 items-center gap-3 shadow-xl">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-400" /></div>
                <div>
                  <div className="text-sm font-semibold text-white">{t("hero.activated")}</div>
                  <div className="text-[11px] text-slate-400">in 1m 47s · user_rahul_22</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-3 bg-gradient-to-r from-[#0e1a36] via-[#15244a] to-[#0e1a36] border-y border-amber-500/10 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 text-sm text-amber-200/90">
              <span>{t("marquee.instantId")}</span><span>●</span>
              <span>{t("marquee.games")}</span><span>●</span>
              <span>{t("marquee.bonus")}</span><span>●</span>
              <span>{t("marquee.withdrawal")}</span><span>●</span>
              <span>{t("marquee.secure")}</span><span>●</span>
              <span>{t("marquee.support")}</span><span>●</span>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      {stats.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((s) => (
              <div key={s.label} className="card-dark p-6 text-center">
                <div className="text-3xl md:text-4xl font-extrabold brand-gradient">{s.value}</div>
                <div className="text-slate-400 text-xs md:text-sm uppercase tracking-wider mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHY CHOOSE */}
      {features.length > 0 && (
        <section className="section-pad">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.whyChooseBadge")}</Badge>
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.whyChooseTitle", { count: site.users, brand: site.brand })}</h2>
              <p className="mt-3 text-slate-400">{t("sections.whyChooseSubtitle")}</p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((f) => (
                <div key={f.title} className="card-dark p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                    <Icon name={f.icon} className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section className="section-pad bg-gradient-to-b from-transparent via-[#0c1530]/40 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.servicesBadge")}</Badge>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-white">{t("sections.servicesTitle")}</h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm">{t("sections.servicesSubtitle")}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="card-dark p-6 group">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/25 to-amber-400/10 border border-amber-500/30 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Icon name={s.icon} className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GAMES */}
      <section className="section-pad" data-testid="games-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.gamesBadge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.gamesTitle")}</h2>
            <p className="mt-3 text-slate-400">{t("sections.gamesSubtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {GAMES.map((g) => (
              <a
                key={g.name}
                href={site.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`game-card-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative rounded-2xl overflow-hidden border border-amber-500/20 aspect-square bg-[#0c1530] shadow-lg shadow-black/30 hover:border-amber-500/50 transition-colors"
              >
                <img src={g.img} alt={g.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1226] via-[#0a1226]/30 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wide bg-black/55 backdrop-blur-md text-amber-300 border border-amber-500/30 rounded-full px-2.5 py-1">
                  {g.tag}
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight">{g.name}</h3>
                  <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-amber-300 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <MessageCircle className="w-3.5 h-3.5" /> {t("sections.gamesCta")} <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      {steps.length > 0 && (
        <section className="section-pad">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.startedBadge")}</Badge>
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.startedTitle", { brand: site.brand })}</h2>
              <p className="mt-3 text-slate-400">{t("sections.startedSubtitle")}</p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {steps.map((s) => (
                <div key={s.n} className="card-dark p-6 relative">
                  <div className="text-5xl font-extrabold text-amber-400/20 leading-none">{s.n}</div>
                  <h3 className="mt-2 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-xl">
                <MessageCircle className="w-5 h-5" /> {t("sections.startOnWa")}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* EXCHANGES */}
      {exchanges.length > 0 && (
        <section className="section-pad bg-gradient-to-b from-transparent via-[#0c1530]/40 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.exchangesBadge")}</Badge>
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.exchangesTitle")}</h2>
              <p className="mt-3 text-slate-400">{t("sections.exchangesSubtitle")}</p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {exchanges.map((e) => (
                <div key={e.name} className="card-dark p-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/30 to-amber-400/10 border border-amber-500/30 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="mt-4 text-base font-extrabold text-white tracking-tight">{e.name}</h3>
                  <div className="mt-3 space-y-1.5 text-xs text-slate-400">
                    <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> {e.bonus}</div>
                    <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> {e.commission}</div>
                  </div>
                  {(e.user || e.pass) && (
                    <div className="mt-4 p-3 rounded-lg bg-black/40 border border-white/5 text-xs">
                      <div className="text-slate-400">{t("cards.demoUser")}: <span className="text-amber-300 font-mono">{e.user}</span></div>
                      <div className="text-slate-400 mt-0.5">{t("cards.demoPass")}: <span className="text-amber-300 font-mono">{e.pass}</span></div>
                    </div>
                  )}
                  <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-outline-gold mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg w-full text-sm">
                    {t("cards.getId")} <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.reviewsBadge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.reviewsTitle")}</h2>
            <p className="mt-3 text-slate-400">{t("sections.reviewsSubtitle")}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="card-dark p-5">
                <div className="flex items-center gap-1">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">“{t.text}”</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-[#0a1226] font-bold text-sm">{t.name[0]}</div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-[11px] text-slate-400">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-gradient-to-b from-transparent via-[#0c1530]/50 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full">{t("sections.faqBadge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white">{t("sections.faqTitle")}</h2>
            <p className="mt-3 text-slate-400">{t("sections.faqSubtitle", { brand: site.brand })}</p>
          </div>
          <Accordion type="single" collapsible className="mt-8 space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="card-dark px-5 border-0">
                <AccordionTrigger className="text-left text-white font-semibold hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-300 text-sm leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#15244a] via-[#0f1a36] to-[#0c1530] border border-amber-500/25 p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight">
                  {t("sections.ctaTitle", { brand: site.brand })}
                </h2>
                <p className="mt-3 text-slate-300 max-w-xl">
                  {t("sections.ctaSubtitle")}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1"><Clock className="w-3.5 h-3.5 text-amber-400" /> {t("chips.activation")}</span>
                  <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1"><ShieldCheck className="w-3.5 h-3.5 text-amber-400" /> {t("chips.ssl")}</span>
                  <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1"><Wallet className="w-3.5 h-3.5 text-amber-400" /> {t("chips.minDeposit")}</span>
                </div>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl w-full md:w-auto">
                  <MessageCircle className="w-5 h-5" /> {t("sections.ctaButton")}
                </a>
                <a href={site.telegramUrl} target="_blank" rel="noreferrer" className="btn-outline-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl w-full md:w-auto">
                  {t("sections.ctaTelegram")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
